/**
 * Board / Timeline shot shortcuts — the PURE half (20260923, owner keyboard-shortcut batch).
 *
 *   ⌘C / Ctrl+C          copy the selected shot (or select mode's ticked set) onto Unity's copy board
 *   ⌘V / Ctrl+V          paste the copy board after the selected shot
 *   ⌘⌥→ / ⌘⌥←            next / previous shot — Board AND Timeline routes
 *
 * No React and no store imports on purpose: everything here takes its data in, so the decision
 * logic is testable without mounting a board, and the Timeline hook (useKeyboardShortcuts) can share
 * `adjacentShotId` without dragging the board's hook — or its stores — into its import graph.
 * The listeners themselves live in useBoardShotShortcuts.ts (board) and useKeyboardShortcuts.ts
 * (timeline).
 */

export type ShotShortcut = 'copy' | 'paste' | 'nextShot' | 'prevShot';

/**
 * Which shot shortcut a key event names, or null. Cmd (or Ctrl, for the standalone web build on
 * other platforms) is required and Shift must be up (⌘⇧C / ⌘⇧V are not ours).
 *
 * Copy / paste are the plain chords only: Option up too — ⌘⌥V is the AI workspace's Alt+V on the
 * Timeline, and no chord here should shadow it. Next / previous are ⌘⌥→ / ⌘⌥← (owner, 20260923):
 * Option is what keeps them off plain ⌘→ / ⌘←, a text field's end / start of line. The arrow is
 * read from the physical `code` first and from the produced `key` only when the code names no arrow.
 */
export function resolveShotShortcut(
  e: Pick<KeyboardEvent, 'code' | 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>,
): ShotShortcut | null {
  if (!(e.metaKey || e.ctrlKey) || e.shiftKey) return null;
  if (e.altKey) {
    const arrow = e.code === 'ArrowRight' || e.code === 'ArrowLeft' ? e.code : e.key;
    if (arrow === 'ArrowRight') return 'nextShot';
    if (arrow === 'ArrowLeft') return 'prevShot';
    return null;
  }
  if (e.code === 'KeyC') return 'copy';
  if (e.code === 'KeyV') return 'paste';
  return null;
}

/** Everything a shot shortcut must stay out of: a text field (its own ⌘C / ⌘V are the user's), a
 *  select, a contenteditable, and anything inside a dialog — a modal owns its keys, and focus parked
 *  on one of its buttons must not let ⌘V paste a shot onto the board behind it. */
const EDITABLE_OR_DIALOG_SELECTOR = [
  'input',
  'textarea',
  'select',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[contenteditable="plaintext-only"]',
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[aria-modal="true"]',
  'dialog',
].join(', ');

/**
 * True when the event's target is (or sits inside) something that owns its own copy / paste / keys.
 * A clipboard event's target can be a Text node (the selection's anchor), so a non-element node is
 * judged by its parent element. `closest` is feature-checked because the target of a window-level
 * event is not always an Element (it can be the window or the document itself).
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (target == null) return false;
  let el: Element | null = null;
  if (typeof Element !== 'undefined' && target instanceof Element) el = target;
  else if (typeof Node !== 'undefined' && target instanceof Node) el = target.parentElement;
  if (el == null) return false;
  if ((el as HTMLElement).isContentEditable === true) return true;
  if (typeof el.closest !== 'function') return false;
  return el.closest(EDITABLE_OR_DIALOG_SELECTOR) != null;
}

/** A non-empty page text selection — the case where ⌘C must stay the browser's text copy. Guarded
 *  because jsdom and some WebView builds hand back no Selection (or throw on toString). */
export function hasTextSelection(): boolean {
  if (typeof window === 'undefined' || typeof window.getSelection !== 'function') return false;
  try {
    const sel = window.getSelection();
    if (sel == null) return false;
    return sel.toString().length > 0;
  } catch {
    return false;
  }
}

/**
 * The shot one step forward / back from `selectedId` in `orderedIds`.
 *   · No selection (or an id the list no longer holds — a repush removed it): forward lands on the
 *     FIRST shot and backward on the LAST, so the very first press always selects something.
 *   · At either end: null. No wrap — a wrap would jump the board from its last scene to its first,
 *     and the owner's CAM-mode twin on the canvas stops at the ends too.
 */
export function adjacentShotId(orderedIds: readonly number[], selectedId: number | null, forward: boolean): number | null {
  if (orderedIds.length === 0) return null;
  const idx = selectedId == null ? -1 : orderedIds.indexOf(selectedId);
  if (idx < 0) return forward ? orderedIds[0] : orderedIds[orderedIds.length - 1];
  const next = idx + (forward ? 1 : -1);
  if (next < 0 || next >= orderedIds.length) return null;
  return orderedIds[next];
}

/**
 * True when an action that last ran at `last` should NOT run again at `now`. The board listens for
 * both the ⌘C / ⌘V keydown and the `copy` / `paste` clipboard events, because on macOS Standalone
 * NativeWebViewMac dispatches plain ⌘C / ⌘V natively as `copy:` / `paste:` and the page never sees
 * the keydown. An engine that delivers BOTH for one press must still run the action once. A clock
 * that stepped backwards (`now < last`) never dedupes — a missed dedupe costs a second no-op copy,
 * a wrong one swallows a real press.
 */
export function shouldDedupe(now: number, last: number | null, windowMs = 250): boolean {
  if (last == null) return false;
  if (now < last) return false;
  return now - last < windowMs;
}
